import { useNotificationStore } from 'Modules/notification/store';
import { ANALYTICS_EVENT_SOFTPHONE } from 'Modules/softphone/constants/analytics';
import { SOFTPHONE_SHORTCUTS } from 'Modules/softphone/constants/keyboardShortcuts';
import { useSoftphoneCallControls } from 'Modules/softphone/hooks/useSoftphoneCallControls';
import { CallManager } from 'Modules/softphone/services/CallManager';
import {
  selectPermissions,
  selectWarmTransferConsultation,
  useSoftphoneStore,
} from 'Modules/softphone/store';
import type { SoftphoneShortcutAction } from 'Modules/softphone/types';
import { keyboardShortcutsLogger as logger } from 'Modules/softphone/utils/logger';
import { completeWarmTransfer } from 'Modules/softphone/utils/warmTransfer';
import { useEffect, useRef } from 'react';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { isEditableTarget } from 'Utils/isEditableTarget';

type EstablishedCallAction = Exclude<
  SoftphoneShortcutAction,
  'accept' | 'decline'
>;

type CallControls = ReturnType<typeof useSoftphoneCallControls>;

const trackIncomingCall = (action: 'accept' | 'decline', callId: string) => {
  logger.info(`Shortcut triggered: ${action}`, { callId });
  pushToGTMDataLayer(
    action === 'accept'
      ? ANALYTICS_EVENT_SOFTPHONE.SHORTCUT_ACCEPT_TRIGGERED
      : ANALYTICS_EVENT_SOFTPHONE.SHORTCUT_DECLINE_TRIGGERED,
    { call_id: callId }
  );
};

/**
 * Video conference invites are not SIP calls — they never enter
 * `incomingCalls`, they only exist as the banner notification whose
 * `onAccept`/`onDecline` VideoConferenceHandler wired to Join/Decline. So the
 * shortcut fires the exact same callbacks the banner buttons do.
 *
 * Returns whether the shortcut actually fired.
 */
const triggerVideoInvite = (action: 'accept' | 'decline'): boolean => {
  const invite = (
    useNotificationStore.getState().notifications.get('tr') ?? []
  ).find((n) => n.inboundCallData?.isVideo)?.inboundCallData;

  const handler = action === 'accept' ? invite?.onAccept : invite?.onDecline;
  if (!handler) {
    return false;
  }

  handler();
  trackIncomingCall(action, invite.callId);
  return true;
};

/** Returns whether the shortcut actually fired (call was in a valid state). */
const triggerIncomingCall = (action: 'accept' | 'decline'): boolean => {
  const { incomingCalls } = useSoftphoneStore.getState();
  const [callId] = incomingCalls.keys();

  if (!callId) {
    return triggerVideoInvite(action);
  }

  const callManager = CallManager.getInstance();
  void (action === 'accept'
    ? callManager.acceptCall(callId)
    : callManager.declineCall(callId));

  trackIncomingCall(action, callId);
  return true;
};

/** Returns whether the shortcut actually fired (permission allowed it). */
const triggerEstablishedCall = (
  action: EstablishedCallAction,
  controls: CallControls
): boolean => {
  const state = useSoftphoneStore.getState();
  const permissions = selectPermissions(state);
  // A warm transfer consultation replaces the grid's Transfer/Merge with its
  // own row, so the chords follow the buttons: Transfer completes the handover
  // instead of reopening the transfer panel, and Merge is off-limits (it would
  // conference both legs instead of handing the call over). End needs no
  // remapping — the consultation *is* the current call, so hanging it up is
  // exactly what the row's Hang up button does.
  const consultation = selectWarmTransferConsultation(state);

  const canRun: Record<EstablishedCallAction, boolean> = {
    mute: permissions.canMute,
    hold: permissions.canHold,
    end: permissions.canHangUp,
    keypad: permissions.canUseKeypad,
    // Video has extra disable reasons beyond the base permission (no Pro
    // package, external contact, conference membership — see
    // useVideoPermission), so gate on the same computed flag the button uses.
    video: controls.isVideoButtonActive,
    transfer: consultation.isActive
      ? consultation.canComplete
      : permissions.canTransfer,
    merge: !consultation.isActive && permissions.canMerge,
  };

  if (!canRun[action]) {
    return false;
  }

  const run: Record<EstablishedCallAction, () => void> = {
    mute: () => controls.handleMute(),
    hold: () => void controls.handleHold(),
    end: () => void controls.handleEndCall(),
    keypad: () => controls.handleKeypad(),
    video: () => void controls.handleVideo(),
    transfer: consultation.isActive
      ? () => void completeWarmTransfer()
      : () => controls.handleTransfer(),
    merge: () => void controls.handleMerge(),
  };
  run[action]();

  const gaEvent: Record<EstablishedCallAction, string> = {
    mute: ANALYTICS_EVENT_SOFTPHONE.SHORTCUT_MUTE_TRIGGERED,
    hold: ANALYTICS_EVENT_SOFTPHONE.SHORTCUT_HOLD_TRIGGERED,
    end: ANALYTICS_EVENT_SOFTPHONE.SHORTCUT_END_TRIGGERED,
    keypad: ANALYTICS_EVENT_SOFTPHONE.SHORTCUT_KEYPAD_TRIGGERED,
    video: ANALYTICS_EVENT_SOFTPHONE.SHORTCUT_VIDEO_TRIGGERED,
    transfer: ANALYTICS_EVENT_SOFTPHONE.SHORTCUT_TRANSFER_TRIGGERED,
    merge: ANALYTICS_EVENT_SOFTPHONE.SHORTCUT_MERGE_TRIGGERED,
  };

  const callId = useSoftphoneStore.getState().currentCallId;
  logger.info(`Shortcut triggered: ${action}`, { callId });
  pushToGTMDataLayer(gaEvent[action], { call_id: callId });
  return true;
};

/**
 * Registers the global keyboard shortcuts for the
 * softphone's call actions: Mute, Hold, Accept, Decline, End, Keypad, Video, Transfer, Merge.
 *
 * A single `keydown` listener is attached for the lifetime of `enabled`.
 * Each shortcut re-uses the same handler/permission checks as its on-screen
 * button (FR6, FR10) and is a silent no-op outside its valid call state.
 *
 * @param enabled Gate for the "new softphone" feature flag — pass `false`
 * to keep the hook mounted (satisfying rules-of-hooks) without attaching
 * the listener.
 */
export const useSoftphoneKeyboardShortcuts = (enabled: boolean): void => {
  const callControls = useSoftphoneCallControls();
  // Read the latest handlers/permissions at keydown time without needing to
  // resubscribe the window listener on every render (some of these handlers,
  // e.g. handleKeypad/handleTransfer, are not referentially stable).
  const callControlsRef = useRef(callControls);
  callControlsRef.current = callControls;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Holding the chord makes the OS auto-repeat `keydown` every few tens of
      // milliseconds. Only the initial press counts — otherwise holding e.g.
      // Alt+Shift+M for a second toggles mute dozens of times (and fires as
      // many analytics events).
      if (event.repeat) {
        return;
      }

      // Exact modifier match only: Alt+Shift held, Ctrl/Meta never involved.
      if (!event.altKey || !event.shiftKey || event.ctrlKey || event.metaKey) {
        return;
      }

      const shortcut = SOFTPHONE_SHORTCUTS.find((s) => s.code === event.code);
      if (!shortcut) {
        return;
      }

      // Never intercept the chord while the user is typing (FR7) — critical
      // on macOS, where Option-based chords can still produce characters.
      if (isEditableTarget(event.target)) {
        return;
      }

      const action: SoftphoneShortcutAction = shortcut.action;

      const didTrigger =
        action === 'accept' || action === 'decline'
          ? triggerIncomingCall(action)
          : triggerEstablishedCall(action, callControlsRef.current);

      // A shortcut pressed outside its valid call state is a silent no-op
      // (FR6) — only swallow the keystroke when it actually did something,
      // so an unrelated Alt+Shift+<letter> press is left untouched (FR8).
      if (didTrigger) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled]);
};
